/**
 * ProjectDraftCardConnected — wrapper que conecta hooks de project outreach al DraftCard shared.
 * Sprint 7.6 Bloque A.
 */
import { useTranslation } from 'react-i18next';
import { DraftCard } from '../outreach/DraftCard.js';
import type { DraftCardLabels, NormalizedDraft } from '../outreach/types.js';
import {
  useSendProjectDraft,
  useCancelProjectDraft,
  useUpdateProjectDraft,
  useSendProjectManualEmail,
  useSkipProjectDraft,
  useRegenerateProjectDraft,
  useFollowupProjectDraft,
  type ProjectOutreachDraft,
} from '../../hooks/useProjectOutreach.js';

interface ProjectDraftCardConnectedProps {
  draft: ProjectOutreachDraft;
  projectId: number;
}

export function ProjectDraftCardConnected({ draft, projectId }: ProjectDraftCardConnectedProps) {
  const { t } = useTranslation();
  const send = useSendProjectDraft(projectId);
  const cancel = useCancelProjectDraft(projectId);
  const update = useUpdateProjectDraft(projectId);
  const sendManual = useSendProjectManualEmail(projectId);
  const skip = useSkipProjectDraft(projectId);
  const regenerate = useRegenerateProjectDraft(projectId);
  const followup = useFollowupProjectDraft(projectId);

  const normalized: NormalizedDraft = {
    id: draft.id,
    draftType: draft.draftType,
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    bodyText: draft.bodyText,
    status: draft.status,
    sendError: draft.sendError,
    sentAt: draft.sentAt,
    createdAt: draft.createdAt,
    sequenceOrder: draft.sequenceOrder,
    scheduledFor: draft.scheduledFor,
    sentMessageId: draft.sentMessageId,
  };

  const labels: DraftCardLabels = {
    initial: t('projects.outreach.draftType.new'),
    followupN: t('projects.outreach.draftType.followup'),
    newEmail: t('projects.outreach.draftType.new'),
    briefRequest: t('projects.outreach.draftType.briefRequest'),
    contentPlanReview: t('projects.outreach.draftType.contentPlanReview'),
    designReview: t('projects.outreach.draftType.designReview'),
    replyType: t('projects.outreach.draftType.reply'),
    editBtn: t('projects.outreach.editBtn'),
    editSave: t('projects.outreach.editSave'),
    editCancel: t('common.cancel'),
    sendBtn: t('projects.outreach.sending'),
    sendingLabel: t('projects.outreach.composing'),
    sendNow: t('projects.outreach.sending'),
    cancelBtn: t('projects.outreach.cancelConfirm'),
    skipBtn: t('projects.outreach.skipBtn'),
    retryBtn: t('outreach.actions.retrySend'),
    regenerateBtn: t('projects.outreach.regenerateBtn'),
    followupBtn: t('projects.outreach.followupBtn'),
    expandBtn: t('projects.outreach.expandBtn'),
    collapseBtn: t('projects.outreach.collapseBtn'),
    copyBtn: t('projects.outreach.copyBtn'),
    copied: t('projects.outreach.copied'),
    replyBtn: t('projects.outreach.reply', { defaultValue: 'Reply' }),
    sendReplyBtn: t('projects.outreach.sendReplyBtn'),
    sentAgo: t('projects.outreach.sent_ago', { time: '{time}' }),
    scheduledForLabel: '',
    editSubjectLabel: t('projects.outreach.editSubjectLabel'),
    editBodyLabel: t('projects.outreach.editBodyLabel'),
    actionFailed: t('projects.outreach.action_failed'),
    composerPlaceholder: t('projects.outreach.composing'),
    failedStatus: t('outreach.status.failed'),
    cancelledStatus: 'CANCELLED',
  };

  return (
    <DraftCard
      draft={normalized}
      labels={labels}
      useRichTextEditor={false}
      onSend={() => send.mutate(draft.id)}
      onSkip={() => skip.mutate(draft.id)}
      onRegenerate={() => regenerate.mutate(draft.id)}
      onFollowup={() => followup.mutate({ draftId: draft.id })}
      onUpdate={({ subject, bodyHtml, bodyText }) =>
        update.mutate({ draftId: draft.id, subject, bodyHtml, bodyText })
      }
      onReply={({ subject, bodyHtml, bodyText, inReplyTo, references }) =>
        sendManual.mutate({ subject, bodyHtml, bodyText, ...(inReplyTo ? { inReplyTo, references } : {}) })
      }
      sendState={{ isPending: send.isPending, isError: send.isError, error: send.error }}
      cancelState={{ isPending: cancel.isPending, isError: cancel.isError, error: cancel.error }}
      skipState={{ isPending: skip.isPending, isError: skip.isError, error: skip.error }}
      updateState={{ isPending: update.isPending, isError: update.isError, error: update.error }}
      replyState={{ isPending: sendManual.isPending, isError: sendManual.isError, error: sendManual.error }}
      regenerateState={{ isPending: regenerate.isPending, isError: regenerate.isError, error: regenerate.error }}
      followupState={{ isPending: followup.isPending, isError: followup.isError, error: followup.error }}
    />
  );
}
